import React, { createContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Input } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { SearchIcon } from "@monorepo/icons";
import { nullService } from "@monorepo/slices/service";
import { KeyboardKeysEnum } from "@monorepo/types";
import Box from "@mui/material/Box";
import debounce from "lodash.debounce";
import { reverse } from "named-urls";

import { useAppDispatch } from "hooks";

import { ServiceListAutocompleteResult } from "./ServiceListAutocompleteResult";

interface IServiceListAutocomplete {
  initialValue?: string;
  disabled?: boolean;
  divided?: boolean;
  autofocus?: boolean;
  categoryId?: number;
  pathToPush?: (serviceId: string) => string;
}

interface IServiceListAutocompleteContext {
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  setActiveServiceId: React.Dispatch<React.SetStateAction<string>>;
  setServicesCount: React.Dispatch<React.SetStateAction<number>>;
  activeIndex: number;
  arrowsNavigationActive: boolean;
  setArrowsNavigationActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ServiceListAutocompleteContext = createContext({} as IServiceListAutocompleteContext);

export const ServiceListAutocomplete: React.FC<IServiceListAutocomplete> = ({ autofocus = false, ...props }) => {
  const { initialValue, divided = false, disabled = false, categoryId, pathToPush } = props;
  const [listOpen, setListOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(initialValue ? initialValue : "");
  const history = useHistory();
  const [debouncedValue, setDebouncedValue] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [activeServiceId, setActiveServiceId] = useState<string>("");
  const [servicesCount, setServicesCount] = useState<number>(0);
  const [arrowsNavigationActive, setArrowsNavigationActive] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const debounceHandler = debounce((value) => setDebouncedValue(value), 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setListOpen(true);

    if (activeIndex !== -1) {
      setActiveIndex(-1);
    }
    setSearchValue(value);

    if (value.length > 2) {
      debounceHandler(value);
    } else {
      debounceHandler("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ([KeyboardKeysEnum.ARROW_DOWN, KeyboardKeysEnum.ARROW_UP].includes(e.key as KeyboardKeysEnum)) {
      e.preventDefault();
      setArrowsNavigationActive(true);
    }

    if (e.key === KeyboardKeysEnum.ARROW_DOWN) {
      if (activeIndex < servicesCount - 1) {
        setActiveIndex((prev) => prev + 1);
      } else {
        setActiveIndex(0);
      }
    }

    if (e.key === KeyboardKeysEnum.ARROW_UP) {
      if (activeIndex === 0) {
        setActiveIndex(servicesCount - 1);
      } else {
        setActiveIndex((prev) => prev - 1);
      }
    }
  };

  return (
    <ServiceListAutocompleteContext.Provider
      value={{
        setActiveIndex,
        activeIndex,
        setSearchValue,
        setActiveServiceId,
        setServicesCount,
        arrowsNavigationActive,
        setArrowsNavigationActive,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "100%",
          gridTemplateRows: "max-content",
          justifyContent: "stretch",
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
            },
            minWidth: {
              sm: divided ? "485px" : "none",
              lg: divided ? "600px" : "none",
            },
            maxWidth: {
              xs: "none",
              sm: "485px",
              lg: "600px",
            },
            justifySelf: "center",
          }}
        >
          <Input
            disabled={disabled}
            label={t("service.autocomplete_receiver_label")}
            placeholder={t("service.autocomplete_type_service_name_placeholder")}
            value={searchValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoCorrect="off"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            onKeyPress={(e) => {
              if (e.key === KeyboardKeysEnum.ENTER && activeServiceId) {
                setListOpen(false);
                history.push(
                  pathToPush
                    ? pathToPush(activeServiceId)
                    : reverse(routeConfig.dashboard.payments.addService.serviceId, {
                        serviceId: activeServiceId,
                      })
                );
              }
            }}
            autoFocus={autofocus}
            ref={inputRef}
            icon={disabled ? null : <SearchIcon fontSize="small" />}
          />

          {((!divided && listOpen) || divided) && (
            <ServiceListAutocompleteResult
              filterId={categoryId}
              divided={divided}
              onServiceSelect={(id, name) => {
                setListOpen(false);
                setSearchValue(name);
                dispatch(nullService());
                history.push(
                  pathToPush
                    ? pathToPush(id)
                    : reverse(routeConfig.dashboard.payments.addService.serviceId, {
                        serviceId: id,
                      })
                );
              }}
              debouncedValue={debouncedValue}
              setDebouncedValue={setDebouncedValue}
              searchValue={searchValue}
            />
          )}
        </Box>
      </Box>
    </ServiceListAutocompleteContext.Provider>
  );
};

export default ServiceListAutocomplete;
